import { Conversation } from "@twilio/conversations";
import { Dispatch } from "redux";

import { ACTION_UPDATE_CONVERSATION_STATE, ACTION_UPDATE_AWAITING_BOT, ACTION_UPDATE_HUMAN_MODE, ACTION_UPDATE_AGENT_TYPING, ACTION_UPDATE_CSAT_PENDING } from "../actionTypes";

export const initConversationListener = (conversation: Conversation, dispatch: Dispatch) => {
    /*
     * HIDRATAÇÃO: o que já estava na conversa quando esta página carregou.
     *
     * O `addListener("updated", ...)` abaixo só entrega eventos FUTUROS. Sem isto, a macro do
     * atendente carimba a pesquisa, o apostador recarrega a página, o listener é registrado depois —
     * e nenhum update novo acontece. O sinal nunca entra no estado e a pesquisa não aparece, sem
     * erro e sem log.
     *
     * Hidrata SÓ o `csatPending`, de propósito. Os outros três atributos que este listener lê são
     * transitórios: ressuscitar `botTyping` ou `agentTyping` ao carregar mostraria um balão de
     * "digitando" de quem parou de digitar há horas, e `humanMode` tem um segundo sinal (participante
     * que não é o cliente) com o qual isto competiria. A pesquisa pendente é a única DURÁVEL por
     * natureza — ela vale até ser respondida.
     */
    const atributosIniciais = (conversation.attributes ?? {}) as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(atributosIniciais, "csatPending")) {
        dispatch({
            type: ACTION_UPDATE_CSAT_PENDING,
            payload: {
                csatPending: atributosIniciais.csatPending === true,
                conversationSid: conversation.sid
            }
        });
    }

    conversation.addListener("updated", ({ conversation: updatedConversation, updateReasons }) => {
        // we are listening only to a subset of events.
        if (updateReasons?.includes("state")) {
            // SID junto: o evento é assíncrono e pode chegar depois de o apostador já estar em outra conversa.
            dispatch({
                type: ACTION_UPDATE_CONVERSATION_STATE,
                payload: {
                    conversationState: updatedConversation?.state?.current,
                    conversationSid: updatedConversation?.sid ?? conversation.sid
                }
            });
        }

        /*
         * The backend drives the "waiting for bot" state via conversation.attributes.botTyping
         * (true while generating, false when done). We only react when the botTyping KEY is
         * present, so unrelated attribute updates (e.g. taskSid) never release the lock by mistake.
         */
        if (updateReasons?.includes("attributes")) {
            const attributes = (updatedConversation?.attributes ?? {}) as Record<string, unknown>;
            if (Object.prototype.hasOwnProperty.call(attributes, "botTyping")) {
                dispatch({
                    type: ACTION_UPDATE_AWAITING_BOT,
                    payload: { awaitingBot: attributes.botTyping === true }
                });
            }
            /*
             * Transbordo: o synccx-api carimba handoff=true (+ botTyping=false). A partir daí
             * é modo humano — o widget para de mostrar balão e de travar o input.
             *
             * E o caminho de VOLTA: quando o transbordo é desfeito (a task morreu sem ninguém
             * aceitar e o bot reassume), o backend carimba handoff=false. Reagimos à PRESENÇA da
             * chave — igual ao botTyping acima —, nunca a `!== true`: conversa que nunca foi
             * transbordada não tem a chave, e um update de atributo alheio não pode derrubar o
             * modo humano por tabela.
             *
             * ⚠️ Sair daqui NÃO garante `isHumanMode === false`: o seletor tem um segundo sinal
             * (participante que não é o cliente). Isso é proposital — se um atendente entrou de
             * fato, ele manda; o `participantLeft` é que zera aquele lado.
             */
            if (Object.prototype.hasOwnProperty.call(attributes, "handoff")) {
                dispatch({
                    type: ACTION_UPDATE_HUMAN_MODE,
                    payload: { humanMode: attributes.handoff === true }
                });
            }

            /*
             * Atendente digitando: o cx-api carimba agentTyping:true/false nos attributes da conversa
             * (o Twilio não tem typing via REST — mesmo padrão do botTyping). O widget mostra o balão.
             */
            if (Object.prototype.hasOwnProperty.call(attributes, "agentTyping")) {
                dispatch({
                    type: ACTION_UPDATE_AGENT_TYPING,
                    payload: { agentTyping: attributes.agentTyping === true }
                });
            }

            /*
             * Pesquisa disparada pela macro do atendente, no meio do atendimento. Mesma forma dos
             * três acima — reage à PRESENÇA da chave, nunca a `!== true` — com uma diferença que não
             * é estilo: este leva o SID.
             *
             * Os outros três são sinais transitórios; um "digitando" atrasado de outra conversa
             * acende um balão e passa. Este é durável: faz uma pesquisa aparecer e ficar. Um evento
             * da conversa anterior aterrissando depois de o apostador abrir outra mostraria a
             * pesquisa do atendimento errado. O reducer usa o SID para descartar esse caso.
             */
            if (Object.prototype.hasOwnProperty.call(attributes, "csatPending")) {
                dispatch({
                    type: ACTION_UPDATE_CSAT_PENDING,
                    payload: {
                        csatPending: attributes.csatPending === true,
                        conversationSid: updatedConversation?.sid ?? conversation.sid
                    }
                });
            }
        }
    });
};
